/* ============================================================
   Rewards Ticker — global JS
   Loaded from layout/theme.liquid so it works whether the
   ticker markup is rendered directly OR cloned out of a
   <template> tag (e.g. cart drawer).
   ============================================================ */
(function () {
  if (window.__rtBooted) return;
  window.__rtBooted = true;

  // Session-scoped flags
  var SS_OPTED_OUT = 'rt:opted-out';
  var SS_HAD_GIFT = 'rt:had-gift';
  var SS_AUTO_CLAIM_FAILED = 'rt:auto-claim-failed';
  var SS_LAST_ATTEMPT = 'rt:last-claim-attempt';

  // Globals for change-detection across re-renders
  window.__rtWasMaskUnlocked = window.__rtWasMaskUnlocked || false;
  window.__rtAutoClaimInFlight = false;

  function getSS(k) { try { return sessionStorage.getItem(k); } catch (_) { return null; } }
  function setSS(k, v) { try { sessionStorage.setItem(k, v); } catch (_) {} }

  function formatMoney(cents) {
    var n = (cents / 100);
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',').replace(/\.00$/, '');
  }

  // theme:cart:refresh is only auto-wired to cart-drawer instances in the core
  // theme bundle, so the cart PAGE's line items never re-render from that event
  // alone. Call getCart() directly on every cart-element on the page as well.
  function refreshCartElements() {
    document.querySelectorAll('cart-element').forEach(function (el) {
      if (typeof el.getCart === 'function') el.getCart();
    });
  }

  // Auto-add the free gift when threshold is first crossed (unless customer opted out).
  // Returns a promise so callers (including the manual "Claim" button) can hook
  // their own UI feedback while sharing these same guards — this is the single
  // path that ever adds the gift, so two triggers can never race into a double-add.
  function tryAutoClaim(giftVariantId) {
    if (!giftVariantId) return Promise.resolve();
    if (window.__rtAutoClaimInFlight) return Promise.resolve();
    if (getSS(SS_OPTED_OUT)) return Promise.resolve();
    if (getSS(SS_AUTO_CLAIM_FAILED)) return Promise.resolve();

    var last = parseInt(getSS(SS_LAST_ATTEMPT) || '0', 10);
    if (Date.now() - last < 4000) return Promise.resolve(); // throttle retries

    window.__rtAutoClaimInFlight = true;
    setSS(SS_LAST_ATTEMPT, String(Date.now()));

    var fetchFn = window.__rtOriginalFetch || window.fetch;
    return fetchFn('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      // _added_via is a hidden line-item property (underscore prefix keeps it
      // out of cart/checkout UI) that persists onto the completed order, so
      // ticker-driven adds can be reported on later via the Admin API.
      body: JSON.stringify({ id: giftVariantId, quantity: 1, properties: { '_added_via': 'rewards_ticker_gift' } })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function () {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        refreshCartElements();
        setTimeout(updateAllTickers, 200);
        setTimeout(updateAllTickers, 700);
      })
      .catch(function (err) { setSS(SS_AUTO_CLAIM_FAILED, '1'); throw err; })
      .finally(function () { window.__rtAutoClaimInFlight = false; });
  }

  // Auto-remove the gift if cart drops below threshold while it's still in cart
  function tryAutoRemove(giftVariantId, cart) {
    if (!giftVariantId) return;
    if (window.__rtAutoRemoveInFlight) return;
    if (!cart || !cart.items) return;

    var line = null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === giftVariantId) { line = cart.items[i]; break; }
    }
    if (!line) return;

    window.__rtAutoRemoveInFlight = true;

    var fetchFn = window.__rtOriginalFetch || window.fetch;
    fetchFn('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: 0 })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function () {
        // Reset session flags so the next threshold cross can auto-claim again
        setSS(SS_HAD_GIFT, '');
        setSS(SS_AUTO_CLAIM_FAILED, '');
        setSS(SS_LAST_ATTEMPT, '0');
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        refreshCartElements();
        setTimeout(updateAllTickers, 200);
        setTimeout(updateAllTickers, 700);
      })
      .catch(function () {})
      .finally(function () { window.__rtAutoRemoveInFlight = false; });
  }

  // "Close the gap" suggestions logic
  var GAP_MIN_DOLLARS = 5;    // show only if at least $5 away
  var GAP_MAX_DOLLARS = 120;  // and within $120 of threshold (covers all 5 tiers)
  var gapCache = { sig: '', items: null };

  function shouldShowGap(remainingCents) {
    var rDollars = remainingCents / 100;
    return rDollars >= GAP_MIN_DOLLARS && rDollars <= GAP_MAX_DOLLARS;
  }

  function gapSignature(cart, remainingCents) {
    var lastId = cart && cart.items && cart.items.length ? cart.items[cart.items.length - 1].product_id : 'none';
    return lastId + ':' + remainingCents;
  }

  function fetchGapSuggestions(cart, remainingCents) {
    if (!cart || !cart.items || !cart.items.length) {
      return Promise.resolve([]);
    }
    var lastItem = cart.items[cart.items.length - 1];
    var productId = lastItem.product_id;
    if (!productId) return Promise.resolve([]);

    var fetchFn = window.__rtOriginalFetch || window.fetch;
    return fetchFn(
      '/recommendations/products.json?product_id=' + encodeURIComponent(productId) +
      '&limit=10&intent=related',
      { headers: { Accept: 'application/json' } }
    )
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (data) {
        var products = (data && data.products) || [];
        // Products (not just exact variants) already in the cart — skip those
        // entirely, regardless of which of their variants is in the cart.
        var inCartProductIds = {};
        cart.items.forEach(function (it) { inCartProductIds[it.product_id] = true; });

        // Filter: must be available, price reasonable, not already in cart, must close enough of the gap
        var minPrice = Math.max(0, remainingCents - 500); // 5$ slack
        var picks = products
          .map(function (p) {
            if (inCartProductIds[p.id]) return null;
            var variants = (p.variants || []).filter(function (v) { return v && v.available; });
            var v = variants[0];
            if (!v) return null;
            if (p.tags && p.tags.indexOf('no-cart-suggest') !== -1) return null;
            return {
              variantId: v.id,
              title: p.title,
              url: p.handle ? '/products/' + p.handle : '#',
              image: p.featured_image || (p.images && p.images[0]) || '',
              price: v.price
            };
          })
          .filter(function (p) { return p && p.price >= minPrice; })
          .sort(function (a, b) { return a.price - b.price; })
          .slice(0, 3);
        return picks;
      })
      .catch(function () { return []; });
  }

  function renderGap(root, cart, remainingCents, rewardLabel) {
    var gapEl = root.querySelector('[data-rt-gap]');
    if (!gapEl) return;

    if (!shouldShowGap(remainingCents)) {
      gapEl.hidden = true;
      return;
    }

    var headline = gapEl.querySelector('[data-rt-gap-headline]');
    var products = gapEl.querySelector('[data-rt-gap-products]');
    if (!headline || !products) return;

    var remainingDollars = Math.ceil(remainingCents / 100);
    headline.innerHTML =
      'Spend <strong>$' + remainingDollars + ' more</strong> → unlock <strong>' + rewardLabel + '</strong>';

    gapEl.hidden = false;

    // Cache check: don't re-fetch if signature unchanged
    var sig = gapSignature(cart, remainingCents);
    if (gapCache.sig === sig && gapCache.items) {
      paintGapProducts(products, gapCache.items);
      return;
    }

    fetchGapSuggestions(cart, remainingCents).then(function (items) {
      gapCache = { sig: sig, items: items };
      paintGapProducts(products, items);
    });
  }

  function paintGapProducts(container, items) {
    if (!items || !items.length) {
      container.innerHTML = '<div class="rt__gap-loading">No suggestions available.</div>';
      return;
    }
    container.innerHTML = '';
    items.forEach(function (p) {
      var row = document.createElement('div');
      row.className = 'rt__gap-row';

      var img;
      if (p.image) {
        img = document.createElement('img');
        img.className = 'rt__gap-img';
        img.src = p.image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 48;
        img.height = 48;
        row.appendChild(img);
      }

      var meta = document.createElement('div');
      meta.className = 'rt__gap-meta';
      var a = document.createElement('a');
      a.className = 'rt__gap-title';
      a.href = p.url;
      a.textContent = p.title;
      a.title = p.title;
      var price = document.createElement('div');
      price.className = 'rt__gap-price';
      price.textContent = formatMoney(p.price);
      meta.appendChild(a);
      meta.appendChild(price);
      row.appendChild(meta);

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rt__gap-add';
      btn.innerHTML =
        '<svg aria-hidden="true" class="icon icon-toggle-plus rt__plus-icon" viewBox="0 0 192 192">' +
          '<path d="M30 96h132M96 30v132" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
        '<span>Add</span>';
      btn.dataset.variantId = p.variantId;
      btn.setAttribute('data-rt-gap-add', '');
      btn.setAttribute('aria-label', 'Add ' + p.title);
      row.appendChild(btn);

      container.appendChild(row);
    });
  }

  // Event delegation for gap "+ Add" buttons
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-rt-gap-add]');
    if (!btn) return;
    var variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) return;

    e.preventDefault();
    btn.disabled = true;
    var orig = btn.innerHTML;
    btn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      // _added_via is a hidden line-item property (underscore prefix keeps it
      // out of cart/checkout UI) that persists onto the completed order, so
      // ticker-driven adds can be reported on later via the Admin API.
      body: JSON.stringify({ id: variantId, quantity: 1, properties: { '_added_via': 'rewards_ticker_gap' } })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        refreshCartElements();
        [200, 700, 1400].forEach(function (d) { setTimeout(updateAllTickers, d); });
      })
      .catch(function () {
        btn.disabled = false;
        btn.innerHTML = orig;
        alert('Could not add. Please try again.');
      });
  });

  function renderTicker(root, cart) {
    if (!root || !root.isConnected) return;

    var maskThreshold    = parseInt(root.dataset.maskThreshold, 10) || 0;
    var shippingThreshold = parseInt(root.dataset.shippingThreshold, 10) || 0;
    var save15Threshold  = parseInt(root.dataset.save15Threshold, 10) || 0;
    var save20Threshold  = parseInt(root.dataset.save20Threshold, 10) || 0;
    var save25Threshold  = parseInt(root.dataset.save25Threshold, 10) || 40000;
    var giftVariantId    = parseInt(root.dataset.giftVariantId, 10) || 0;
    var total = (cart && cart.items_subtotal_price != null) ? cart.items_subtotal_price : 0;

    var maskUnlocked    = maskThreshold > 0    && total >= maskThreshold;
    var shippingUnlocked = shippingThreshold > 0 && total >= shippingThreshold;
    var save15Unlocked  = save15Threshold > 0  && total >= save15Threshold;
    var save20Unlocked  = save20Threshold > 0  && total >= save20Threshold;
    var save25Unlocked  = save25Threshold > 0  && total >= save25Threshold;

    // Progress bar fills toward $400 max
    var pct = Math.min(100, (total / save25Threshold) * 100);

    var giftInCart = false;
    if (giftVariantId && cart && cart.items) {
      giftInCart = cart.items.some(function (i) { return i.variant_id === giftVariantId; });
    }

    var hadGift = getSS(SS_HAD_GIFT) === '1';
    if (giftInCart) setSS(SS_HAD_GIFT, '1');

    if (maskUnlocked && !giftInCart && hadGift) {
      setSS(SS_OPTED_OUT, '1');
      setSS(SS_HAD_GIFT, '');
    }

    if (!maskUnlocked && giftInCart) {
      setSS(SS_HAD_GIFT, '');
      tryAutoRemove(giftVariantId, cart);
    }

    // Single source of truth for auto-claim: unlocked + not already in cart.
    // Deliberately NOT gated on "just crossed" transition state — that state
    // doesn't survive a page reload, which previously caused a fresh add on
    // every refresh whenever the cart was already above threshold.
    if (maskUnlocked && !giftInCart) {
      tryAutoClaim(giftVariantId);
    }

    var justMaskUnlocked = maskUnlocked && !window.__rtWasMaskUnlocked;
    window.__rtWasMaskUnlocked = maskUnlocked;

    var fillEl  = root.querySelector('[data-rt-fill]');
    var hereEl  = root.querySelector('[data-rt-here]');
    var msgEl   = root.querySelector('[data-rt-message]');
    var ctaEl   = root.querySelector('[data-rt-cta]');
    var addedEl = root.querySelector('[data-rt-added]');
    var gapEl   = root.querySelector('[data-rt-gap]');

    if (fillEl) fillEl.style.width = pct + '%';
    if (hereEl) hereEl.style.left = pct + '%';

    // Sync milestone dots
    var dotMap = { mask: maskUnlocked, shipping: shippingUnlocked, save15: save15Unlocked, save20: save20Unlocked, save25: save25Unlocked };
    root.querySelectorAll('[data-rt-dot]').forEach(function (el) {
      el.classList.toggle('is-unlocked', !!dotMap[el.dataset.rtDot]);
    });

    // Sync tier chips
    root.querySelectorAll('[data-rt-tier]').forEach(function (el) {
      el.classList.toggle('is-unlocked', !!dotMap[el.dataset.rtTier]);
    });

    // Root unlocked class (triggers border/glow)
    root.classList.toggle('is-unlocked', maskUnlocked);

    var remaining, rewardLabel;

    if (save25Unlocked) {
      // Zone 6: all tiers unlocked — nothing left to work toward
      if (hereEl) hereEl.hidden = true;
      if (msgEl) {
        msgEl.innerHTML = giftInCart
          ? '&#x1F985; <strong>Maximum Freedom!</strong> 25% off + Free Shipping + Free Mask'
          : '&#x1F985; All rewards unlocked &mdash; enjoy <strong>25% off</strong>, Free Shipping &amp; Free Mask!';
      }
      if (giftInCart) {
        if (ctaEl) ctaEl.hidden = true;
        if (addedEl) addedEl.hidden = false;
      } else {
        if (ctaEl) ctaEl.hidden = false;
        if (addedEl) addedEl.hidden = true;
      }
      if (gapEl) gapEl.hidden = true;

    } else if (save20Unlocked) {
      // Zone 5: $300–$399
      remaining = save25Threshold - total;
      rewardLabel = 'Save 25%';
      if (msgEl) {
        msgEl.innerHTML = '20% off activated! Spend <strong data-rt-remaining>' + formatMoney(remaining) + '</strong> more &rarr; <strong>Save 25%</strong>';
      }
      if (ctaEl) ctaEl.hidden = giftInCart || !maskUnlocked;
      if (addedEl) addedEl.hidden = !giftInCart;
      renderGap(root, cart, remaining, rewardLabel);

    } else if (save15Unlocked) {
      // Zone 4: $250–$299
      remaining = save20Threshold - total;
      rewardLabel = 'Save 20%';
      if (msgEl) {
        msgEl.innerHTML = '15% off activated! Spend <strong data-rt-remaining>' + formatMoney(remaining) + '</strong> more &rarr; <strong>Save 20%</strong>';
      }
      if (ctaEl) ctaEl.hidden = giftInCart || !maskUnlocked;
      if (addedEl) addedEl.hidden = !giftInCart;
      renderGap(root, cart, remaining, rewardLabel);

    } else if (shippingUnlocked) {
      // Zone 3: $200–$249
      remaining = save15Threshold - total;
      rewardLabel = 'Save 15%';
      if (msgEl) {
        msgEl.innerHTML = 'Free Shipping unlocked! Spend <strong data-rt-remaining>' + formatMoney(remaining) + '</strong> more &rarr; <strong>Save 15%</strong>';
      }
      if (ctaEl) ctaEl.hidden = giftInCart || !maskUnlocked;
      if (addedEl) addedEl.hidden = !giftInCart;
      renderGap(root, cart, remaining, rewardLabel);

    } else if (maskUnlocked) {
      // Zone 2: $100–$199
      remaining = shippingThreshold - total;
      rewardLabel = 'Free Shipping';
      if (msgEl) {
        msgEl.innerHTML = giftInCart
          ? 'Free Mask added! Spend <strong data-rt-remaining>' + formatMoney(remaining) + '</strong> more &rarr; <strong>Free Shipping</strong>'
          : 'Spend <strong data-rt-remaining>' + formatMoney(remaining) + '</strong> more &rarr; <strong>Free Shipping</strong>';
      }
      if (giftInCart) {
        if (ctaEl) ctaEl.hidden = true;
        if (addedEl) addedEl.hidden = false;
      } else {
        if (ctaEl) ctaEl.hidden = false;
        if (addedEl) addedEl.hidden = true;
      }
      if (justMaskUnlocked) {
        root.classList.add('rt--just-unlocked');
        setTimeout(function () { root.classList.remove('rt--just-unlocked'); }, 1400);
      }
      renderGap(root, cart, remaining, rewardLabel);

    } else {
      // Zone 1: below $100
      remaining = maskThreshold - total;
      rewardLabel = 'Free Dust Mask';
      if (ctaEl) ctaEl.hidden = true;
      if (addedEl) addedEl.hidden = true;
      if (msgEl) {
        msgEl.innerHTML = 'Spend <strong data-rt-remaining>' + formatMoney(remaining) + '</strong> more &rarr; <strong>Free Dust Mask</strong>';
      }
      renderGap(root, cart, remaining, rewardLabel);
    }

    // Zones 1–5 all set `remaining`; zone 6 (save25Unlocked) hides the
    // marker above instead since there's nothing left to work toward.
    if (hereEl && !save25Unlocked) {
      hereEl.hidden = false;
      var hereTextEl = hereEl.querySelector('[data-rt-here-text]');
      if (hereTextEl) hereTextEl.textContent = formatMoney(remaining) + ' to go';
    }
  }

  var inFlight = false;
  function updateAllTickers() {
    if (inFlight) return;
    var tickers = document.querySelectorAll('[data-rewards-ticker]');
    if (!tickers.length) return;
    inFlight = true;
    var fetchFn = window.__rtOriginalFetch || window.fetch;
    fetchFn('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Disconnect while we make our own DOM changes below, so bodyMO
        // doesn't see them as a new "cart area changed" event and schedule
        // yet another update — that self-triggering loop is what caused the
        // ticker to continuously tear down and rebuild itself (visible as
        // rapid blinking, worst right under the cursor since hover state
        // kept resetting every cycle).
        bodyMO.disconnect();
        try {
          document.querySelectorAll('[data-rewards-ticker]').forEach(function (root) {
            renderTicker(root, cart);
          });
        } finally {
          bodyMO.observe(document.body, MO_OPTIONS);
        }
      })
      .catch(function () {})
      .finally(function () { inFlight = false; });
  }

  // Event delegation for the "Add Free Mask" button
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-rt-add]');
    if (!btn) return;

    var root = btn.closest('[data-rewards-ticker]');
    if (!root) return;
    var giftVariantId = parseInt(root.dataset.giftVariantId, 10) || 0;
    if (!giftVariantId) return;

    e.preventDefault();
    btn.disabled = true;
    var addText = btn.querySelector('.rt__add-text, .rt__gift-claim-text');
    var addLoading = btn.querySelector('.rt__add-loading, .rt__gift-claim-loading');
    if (addText) addText.hidden = true;
    if (addLoading) addLoading.hidden = false;

    // User explicitly claimed — clear any opt-out/failure flags so the shared
    // tryAutoClaim() guards below don't block this deliberate click.
    setSS(SS_OPTED_OUT, '');
    setSS(SS_AUTO_CLAIM_FAILED, '');

    tryAutoClaim(giftVariantId)
      .catch(function () {
        btn.disabled = false;
        if (addText) addText.hidden = false;
        if (addLoading) addLoading.hidden = true;
        alert('Could not add the free gift. Please try again.');
      })
      .finally(function () {
        // Reset the button state once cart eventually refreshes
        setTimeout(function () {
          btn.disabled = false;
          if (addText) addText.hidden = false;
          if (addLoading) addLoading.hidden = true;
        }, 2500);
      });
  });

  // Listen for every cart event the theme might dispatch
  [
    'cart:updated', 'cart:refresh', 'cart:build', 'cart:change',
    'theme:cart:refresh', 'theme:cart:update', 'shopify:cart:update'
  ].forEach(function (evt) {
    document.addEventListener(evt, updateAllTickers);
  });

  var cartMutationRe = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  function stagger() {
    [50, 250, 600, 1200, 2000].forEach(function (d) { setTimeout(updateAllTickers, d); });
  }

  // Wrap fetch globally to catch every cart mutation
  if (!window.__rtFetchPatched) {
    window.__rtFetchPatched = true;
    window.__rtOriginalFetch = window.fetch.bind(window);
    var originalFetch = window.__rtOriginalFetch;
    window.fetch = function () {
      var args = arguments;
      var url = args[0];
      var urlString = typeof url === 'string' ? url : (url && url.url) || '';
      var isCartMutation = cartMutationRe.test(urlString);
      var result = originalFetch.apply(this, args);
      if (isCartMutation) result.then(stagger).catch(function () {});
      return result;
    };
  }

  // Wrap XMLHttpRequest too
  if (!window.__rtXhrPatched) {
    window.__rtXhrPatched = true;
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (typeof url === 'string' && cartMutationRe.test(url)) {
          this.addEventListener('load', stagger);
        }
      } catch (_) {}
      return origOpen.apply(this, arguments);
    };
  }

  var cartAreaSelector =
    '[data-cart-drawer], [data-cart-drawer-body], [data-cart-page], ' +
    '.cart-drawer, .cart, [data-cart-items], [data-cart-line-items], ' +
    '[data-cart-form], [data-cart-subtotal], [data-cart-price-holder]';

  var debounceTimer = null;
  function scheduleUpdate() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(updateAllTickers, 80);
  }

  var MO_OPTIONS = {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['value', 'data-quantity', 'data-line', 'data-cart-item-key']
  };

  // Watch for ticker insertion or any cart-area mutation
  var bodyMO = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];

      var added = m.addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (
          (n.matches && n.matches('[data-rewards-ticker]')) ||
          (n.querySelector && n.querySelector('[data-rewards-ticker]'))
        ) {
          scheduleUpdate();
          return;
        }
      }

      var t = m.target;
      if (t && t.nodeType === 3) t = t.parentElement;
      if (t && t.closest && t.closest(cartAreaSelector)) {
        scheduleUpdate();
        return;
      }

      var removed = m.removedNodes;
      if (removed && removed.length) {
        var parent = m.target;
        if (parent && parent.nodeType === 3) parent = parent.parentElement;
        if (parent && parent.closest && parent.closest(cartAreaSelector)) {
          scheduleUpdate();
          return;
        }
      }
    }
  });

  function start() {
    bodyMO.observe(document.body, MO_OPTIONS);
    updateAllTickers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
